import axios, { type AxiosResponse } from "axios";

import Log from "../../../../Logger";
import { getAccessToken } from "../../../../auth";

export type KintoneAppInfo = {
    kintoneAppId: string
    kintoneAppName: string
    kintoneAppDescription: string
    kintoneAppCreatedAt: string
    kintoneAppModifiedAt: string
    kintoneAppLayoutJson: string
    kintoneAppFieldInfoJson: string
}

/**
 * kintone API のラッパー関数。
 * 指定されたパラメータを元に kintone の REST API を実行し、レスポンスを返す。
 * 有効なメソッドはLantana-APIのreadme参照。typeに応じたパラメータ以外があるとエラーになる
 * 
 * @param {string} type - API の種類を指定する文字列(必須)。
 * @param {string} [kintoneAppsInfoParam=""] - kintone アプリ情報のパラメータ（省略可能）。
 * @param {string} [kintoneAppLayoutParam=""] - kintone アプリのレイアウト情報のパラメータ（省略可能）。
 * @param {string} [kintoneAppFieldInfoParam=""] - kintone アプリのフィールド情報のパラメータ（省略可能）。
 * @returns {Promise<kintoneResponse>} - kintone API のレスポンスオブジェクト。
 * @throws {Error} - API リクエストが失敗した場合、エラーオブジェクトをスローする。
 * 
 * @example
 * try {
 *   const response = await kintoneAPIWrapper("get-form-layout", "" ,"{\"kintoneAppId\": \"2\"}", "");
 *   console.log(response);
 * } catch (error) {
 *   console.error(error);
 * }
 */
export async function kintoneAPIWrapper(
    type: string,
    requestParam: string
): Promise<kintoneResponse> {
    //空文字の場合はプロパティを削除する

    const logger = Log.getLogger();
    logger.api.info("Lantana-API", "Start to request: kintoneAPIWrapper");

    const resp = await mutation<{ data: any, errors: any }>("kintoneRestAPIWrapper", "KintoneRestAPIWrapperInput", { type, requestParam }, ["kintoneResponse"]);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        console.error(resp.data.errors)
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute kintoneToken API: id=${reqid}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw { err: new Error(msg) };
    }

    logger.api.info("Lantana-API", "Succeed to request: kintoneResponseParam", resp.headers);

    return resp.data.data.kintoneRestAPIWrapper;
}

function getEwbReqId(resp: any) {
    return resp?.headers?.["ricoh-ewb-request-id"] ?? "-";
}

const GRAPHQL_API_URL = import.meta.env.VITE_GRAPHQL_API_URL ?? "";

type Input = {
    [key: string]: Input | string | number | boolean
}

type GraphQLBody = any;

type kintoneResponse = {
    kintoneResponse: string
}

function mutation<T>(method: "createDocumentUrl", inputType: "CreateDocumentUrlInput", input: Input, output: string[]): Promise<AxiosResponse<T, GraphQLBody>>;
function mutation<T>(method: "kintoneRestAPIWrapper", inputType: "KintoneRestAPIWrapperInput", input: Input, output: string[]): Promise<AxiosResponse<T, GraphQLBody>>;

async function mutation<T>(method: string, inputType: string, input: Input, output: string[]): Promise<AxiosResponse<T, GraphQLBody>> {
    const logger = Log.getLogger();

    logger.api.info("Cognito", "Start to request: currentSession");
    const token = await getAccessToken();
    logger.api.info("Cognito", "Succeed to request: currentSession");

    const apiResponse: AxiosResponse<T, GraphQLBody> = await axios.post<T>(
        GRAPHQL_API_URL,
        `{"query": "mutation($input:${inputType}!) { ${method}(input:$input) { ${output.join(", ")} }}", "variables": { "input": ${JSON.stringify(input)} }}`,
        {
            headers: {
                "Content-Type": "application/graphql",
                "Authorization": "Bearer " + token,
            }
        }
    );

    return apiResponse;
}